import { useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";
import {
  CompliancePageHeader,
  ComplianceTenantRequiredState,
  useComplianceTenant,
} from "./shared";

/**
 * D6: Right-to-Delete Workflow UI
 * Process erasure requests with cascade preview, double confirmation,
 * legal hold check, and crypto-shred trigger.
 */

interface ErasureRequest {
  id: string;
  personId: string;
  status: string;
  errorMessage: string | null;
  anonymizationToken: string | null;
  tablesProcessed: string[];
  fieldsAnonymized: number;
  createdAt: string;
  completedAt: string | null;
}

export default function DeleteRequestPage() {
  const { tenantId, hasTenant } = useComplianceTenant();
  const [personId, setPersonId] = useState("");
  const [request, setRequest] = useState<ErasureRequest | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRequest = async () => {
    if (!tenantId || !personId.trim()) return;
    setIsChecking(true);
    setError(null);

    try {
      const response = await apiFetch<{ data: ErasureRequest[] }>(`/platform/compliance/erasure-requests?tenant_id=${tenantId}&contact_id=${personId.trim()}`);
      setRequest(response.data[0] ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load erasure request");
    } finally {
      setIsChecking(false);
    }
  };

  const handleCreate = async () => {
    if (!tenantId || !personId.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await apiFetch<{ data: ErasureRequest }>(`/platform/compliance/erasure-requests`, {
        method: "POST",
        body: JSON.stringify({
          tenantId,
          personId: personId.trim(),
        }),
      });
      setRequest(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create erasure request");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <CompliancePageHeader
        icon={<Trash2 className="h-5 w-5 text-[var(--color-primary)]" />}
        title="Right to Delete (GDPR Art. 17)"
        description="This page now uses the real erasure request contract. Preview and direct execution are not exposed here because the backend only supports request lifecycle operations."
      />

      {!hasTenant ? <ComplianceTenantRequiredState /> : null}

      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 font-medium">Contact Lookup</h3>
        <div className="flex gap-2">
          <input
            value={personId}
            onChange={(e) => setPersonId(e.target.value)}
            placeholder="Contact UUID"
            className="flex-1 rounded-md border border-input px-3 py-2 text-sm"
          />
          <button
            onClick={() => void loadRequest()}
            disabled={!hasTenant || isChecking}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
          >
            {isChecking ? "Checking..." : "Lookup"}
          </button>
          <button
            onClick={() => void handleCreate()}
            disabled={!hasTenant || isSubmitting || !personId.trim()}
            className="rounded-md border border-[var(--color-error)]/30 px-4 py-2 text-sm text-[var(--color-error)] disabled:opacity-50"
          >
            {isSubmitting ? "Submitting..." : "Create Request"}
          </button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          The old email preview and one-click delete flow was removed because no matching backend handlers exist. Staff must supply a real person UUID.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-4 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {request ? (
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[var(--color-warning)]" />
            <h3 className="font-medium">Erasure Request Status</h3>
          </div>
          <div className="mt-4 grid gap-2 text-sm text-muted-foreground md:grid-cols-2">
            <p><span className="font-medium text-foreground">Request ID:</span> {request.id}</p>
            <p><span className="font-medium text-foreground">Status:</span> {request.status}</p>
            <p><span className="font-medium text-foreground">Fields anonymized:</span> {request.fieldsAnonymized}</p>
            <p><span className="font-medium text-foreground">Created:</span> {formatDateTime(request.createdAt)}</p>
            <p><span className="font-medium text-foreground">Completed:</span> {request.completedAt ? formatDateTime(request.completedAt) : "Not completed"}</p>
            <p><span className="font-medium text-foreground">Anonymization token:</span> {request.anonymizationToken ?? "Pending"}</p>
          </div>
          <div className="mt-4">
            <p className="text-sm font-medium text-foreground">Processed stores</p>
            {request.tablesProcessed.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {request.tablesProcessed.map((item) => (
                  <span key={item} className="rounded-full bg-muted px-2 py-1 text-xs text-muted-foreground">
                    {item}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">No stores processed yet.</p>
            )}
          </div>
          {request.errorMessage ? (
            <p className="mt-4 rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-3 text-sm text-[var(--color-error)]">
              {request.errorMessage}
            </p>
          ) : null}
          <p className="mt-4 text-xs text-muted-foreground">
            Request execution, retry, and legal-hold investigation remain operator flows. This UI now reflects the backend truth instead of pretending cascade preview and direct delete exist.
          </p>
        </div>
      ) : null}
    </div>
  );
}
